import React from 'react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { Figure } from '../components/case-study/Figure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { ClearDocsPrototype } from '../components/clear-docs/ClearDocsPrototype';
import { DesignVsLiveTable } from '../components/clear-docs/DesignVsLiveTable';
import { ImageFigure } from '../components/case-study/ImageFigure';
import { clearDocsImages } from '../data/clearDocsImages';
import { clearDocsMeta, designVsLiveCalls, desktopOnly, mobileScope, reviewCleanedUp, reviewDecided, reviewFindings } from '../data/clearDocs';
export function ClearDocsCaseStudy() {
  return <CaseStudyShell data-id="element-678" data-mp="1vyi5viw2xd5w">
      <CaseHeader overline="Case study · Mortgage lending · Mobile" title="Clear Docs Mobile: deciding what loan officers don't need on a phone" intro="Clear Docs is the desktop tool loan officers use to collect and review borrower documents. The ask was a mobile version, and the requirements were incomplete. I defined the flows step by step with the product's stakeholder and kept the scope small on purpose: accept, reject or archive a document, and tag it. That work then reshaped how review works on desktop too." meta={clearDocsMeta} data-id="element-679" data-mp="1vy02m2b01yoh" />

      <Figure className="mt-14" caption="The working prototype: the document list and the document viewer. Try the list tabs, the status filters and the viewer tabs. All data is sample data." data-id="element-680" data-mp="2bekq1z3flc00">
        <ClearDocsPrototype data-id="element-681" data-mp="22e66p5a126z5" />
      </Figure>

      <CaseSection index="01" label="The problem" title="A desktop tool doesn't shrink" intro={<>
            <p data-id="element-683" data-mp="qxy9zkha1pve">
              Desktop Clear Docs is dense: requests, draft and final documents, split, merge, page sorting, drop zones,
              bulk actions and previews that only appear on hover.
            </p>
            <p data-id="element-684" data-mp="xdonatcq3vzv">
              The mobile requirements didn't say which of that mattered. Rebuilding all of it on a phone would have been
              slow to ship and hard to use, so the real design question was what to leave out.
            </p>
          </>} data-id="element-682" data-mp="22dd66m1nwdpg">
        <ImageFigure {...clearDocsImages.desktopAudit} dark caption="My audit of the live desktop flows, the reference for every mobile decision." data-id="element-685" data-mp="9pf3ty2zfkbw" />
      </CaseSection>

      <CaseSection index="02" label="Design vs. live" title="I designed from what desktop actually does" data-id="element-686" data-mp="3vwvjckin4iff">
        <p className="max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-687" data-mp="4paqui1a97qf">
          The original desktop design and the live product had drifted apart. Designing mobile from the old design file
          would have promised features that don't exist. So I compared the two, area by area, and decided what mobile
          should do in each one.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-688" data-mp="38at9zowcrens">
          {designVsLiveCalls.map((c, i) => <Reveal key={c.title} delay={i * 0.04} className="h-full" data-id="element-689" data-mp="3h9etpndk8zq5" data-mpi={i}>
              <div className="h-full rounded-2xl bg-surface p-5" data-id="element-690" data-mp="3h7bqu2rb7c40">
                <h3 className="text-sm font-semibold text-fg" data-id="element-691" data-mp="3d79wkenwlkyr">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-692" data-mp="3r72lc4fvdr43">{c.body}</p>
              </div>
            </Reveal>)}
        </div>

        <Figure className="mt-10" caption="The full comparison: 16 areas, each with a call for mobile." data-id="element-693" data-mp="3m6napkmz2xpt">
          <DesignVsLiveTable data-id="element-694" data-mp="4u2o00kdkr0g" />
        </Figure>
      </CaseSection>

      <CaseSection index="03" label="Scope" title="A deliberately small mobile experience" data-id="element-695" data-mp="3vz2tula09sca">
        <p className="max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-696" data-mp="dtptaxg86gys">
          With requirements this incomplete, I worked the flows out iteratively with the product's stakeholder instead
          of waiting for a full spec. A PRD-style document captured the core flows, the target users, and every change
          from desktop, so the scope was written down and agreed.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2" data-id="element-697" data-mp="2d167s2xftd33">
          <Reveal className="h-full" data-id="element-698" data-mp="2h1glk1rh17si">
            <div className="h-full rounded-2xl bg-ink-soft p-6" data-id="element-699" data-mp="2h0f3hdc1fl1a">
              <h3 className="text-sm font-semibold text-ink" data-id="element-700" data-mp="1kbh5zncwl90f">On mobile</h3>
              <ul className="mt-3 space-y-2 text-sm text-fg" data-id="element-701" data-mp="1kdk4kvs4gagr">
                {mobileScope.map(item => <li key={item} className="flex gap-2.5" data-id="element-702" data-mp="1tbvs2oxoys4z">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ink" data-id="element-703" data-mp="1taccz1nrun4s" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </Reveal>
          <Reveal className="h-full" delay={0.05} data-id="element-704" data-mp="34qss0tvez1r4">
            <div className="h-full rounded-2xl bg-surface p-6" data-id="element-705" data-mp="39pcjtawov7re">
              <h3 className="text-sm font-semibold text-fg" data-id="element-706" data-mp="39rymox8mwz3a">Left on desktop or simplified</h3>
              <ul className="mt-3 space-y-2 text-sm text-fg-muted" data-id="element-707" data-mp="3jvx0gdcrt7r9">
                {desktopOnly.map(item => <li key={item} className="flex gap-2.5" data-id="element-708" data-mp="3a410ewr4j7ke">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-fg-faint" data-id="element-709" data-mp="3a2g5v74t7omj" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </Reveal>
        </div>

        <ImageFigure className="mt-10" {...clearDocsImages.coreFlows} padded caption="The three core mobile flows: request, review and upload. The request flow (outlined) got the most detail, since it's where a loan officer acts on the go." data-id="element-710" data-mp="2w4humalsc8b9" />
      </CaseSection>

      <CaseSection index="04" label="The design system" title="Adapting a library that wasn't ready for mobile" intro={<>
            <p data-id="element-712" data-mp="lacc2l3k5n7u">
              High fidelity started in Figma and moved to Claude Design and Claude Code, built on the Clear component
              library, so the prototype uses the same components engineering does.
            </p>
            <p data-id="element-713" data-mp="25v0w536ezjp0">
              The library wasn't fully mobile-ready. The team was still fixing accuracy issues between design and code,
              and there was no mobile tabs component. The prototype uses a two-tab pattern (Draft documents and Final in
              LOS on the list, Preview and Properties in the viewer) while the team decided how tabs should work on
              mobile.
            </p>
          </>} data-id="element-711" data-mp="pds391wuyvd5" />

      <CaseSection index="05" label="Document review" title="One review model: the button you press sets the status" intro={<>
            <p data-id="element-715" data-mp="1d5k4w8uj68ud">
              Mobile's Accept and Reject buttons raised a bigger question: what does reviewing a document actually do? On
              desktop the answer was messy.
            </p>
            <p data-id="element-716" data-mp="19n4lrfmxw11d">
              In mid-July I led the team's alignment sessions and consolidated the decisions around one goal: Clear Docs
              and the Conditions Portal share one document-properties component and one role-aware button and status
              model. Every change was framed as streamlining the workflow, with nothing taken away.
            </p>
          </>} data-id="element-714" data-mp="2okgxtttu5i70">
        <div className="grid gap-4 md:grid-cols-3" data-id="element-717" data-mp="qwmyhqkvlxne">
          {reviewFindings.map((f, i) => <Reveal key={f.title} delay={i * 0.04} className="h-full" data-id="element-718" data-mp="lz385sawluf3" data-mpi={i}>
              <div className={`h-full rounded-2xl p-5 ${f.highlight ? 'bg-coral-soft' : 'bg-surface'}`} data-id="element-719" data-mp="10n7s0zlp0bzg">
                <h3 className={`text-sm font-semibold ${f.highlight ? 'text-rust' : 'text-fg'}`} data-id="element-720" data-mp="vwrd1b284iaz">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-721" data-mp="86dhhakfk1bn">{f.body}</p>
              </div>
            </Reveal>)}
        </div>

        <Reveal className="mt-4" data-id="element-722" data-mp="37k7vt89kugr">
          <div className="rounded-2xl bg-ink p-6 text-white md:p-8" data-id="element-723" data-mp="3du53f0b5daat">
            <p className="text-xs font-medium text-white/70" data-id="element-724" data-mp="3dvzqak5zbchj">The behavioral call</p>
            <p className="mt-2 max-w-3xl text-lg font-semibold leading-snug md:text-xl" data-id="element-725" data-mp="3it88gkr4ngis">
              When the wrong option is one slip away, people pick it. So nobody picks a status anymore. The button sets
              it: Accept, Approve or Ready to Ship, depending on your role. No misclicks, and nothing to think about.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-10 md:grid-cols-2 md:gap-12" data-id="element-726" data-mp="5lenc05s6nyt">
          {[{
          title: 'What we decided',
          items: reviewDecided
        }, {
          title: 'What we cleaned up',
          items: reviewCleanedUp
        }].map(list => <div key={list.title} data-id="element-727" data-mp="fe3vom61blpx">
              <h3 className="text-sm font-semibold text-ink" data-id="element-728" data-mp="kc5ct1jwhayr">{list.title}</h3>
              <ul className="mt-3 border-t border-line" data-id="element-729" data-mp="ocxpk4rzdxzs">
                {list.items.map(item => <li key={item} className="border-b border-line py-3 text-sm leading-relaxed text-fg-muted" data-id="element-730" data-mp="28uqe156gphvo">
                    {item}
                  </li>)}
              </ul>
            </div>)}
        </div>

        <ImageFigure className="mt-12" {...clearDocsImages.beforeViewer} caption="Before: a status dropdown, two overlapping checkboxes, and Send to LOS as its own step." data-id="element-731" data-mp="1zv3b2ey3440i" />

        <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-732" data-mp="2sifyssfzehve">
          <ImageFigure {...clearDocsImages.acceptMoves} caption="Accepting moves a document out of Needs Review and into its category." data-id="element-733" data-mp="2njcsz5s6g7md" />
          <ImageFigure {...clearDocsImages.acceptLands} caption="There, its status reads Reviewed, shown read-only in the list." data-id="element-734" data-mp="2du2y5i5y8auk" />
        </div>

        <ImageFigure className="mt-10" {...clearDocsImages.funnel} caption="The button and status funnel by permission, draft side." data-id="element-735" data-mp="3baifg3smhy5n" />

        <ImageFigure className="mt-10" {...clearDocsImages.buttonRules} caption="Which primary button each role sees, and what it does." data-id="element-736" data-mp="1rcwf515it2c6" />

        <ImageFigure className="mt-10" {...clearDocsImages.decisionsBoard} caption="The decisions board, kept separate on purpose: decided, findings, proposals, open questions and parked items." data-id="element-737" data-mp="21blzd0kw2yvl" />
      </CaseSection>

      <CaseSection index="06" label="Where it stands" title="Work in progress" intro={<>
            <p data-id="element-739" data-mp="25bcvzkx3k981">
              By late June 2026, the mobile flows were defined and documented, and the high-fidelity prototype was in
              progress. By mid-July, the review button and status model was decided, with a few questions still open,
              like how splitting a condition-linked document should behave.
            </p>
            <p data-id="element-740" data-mp="2p1zke7523xgo">
              The work is now in the development queue. It hasn't launched, so there are no results to report yet. What
              this project shows is the part before launch: turning a vague ask and a messy model into decisions
              everyone agreed on, ready for engineering to build.
            </p>
          </>} data-id="element-738" data-mp="2aaz8fs53plfs" />

      <NextCase title="Kordis: an AI CFO whose numbers you can check" href="/work/kordis" data-id="element-741" data-mp="2yr54v6usdtu2" />
    </CaseStudyShell>;
}